import { parseFuelTank, parseFuelType } from '@felix/domain';
import type { FuelTank, FuelTankInput, FuelType, FuelTypeInput } from './types.js';

/**
 * Tanklar ekranının formları (ADR 0117; ekran 02). Kurallar domain şemasından
 * (`parseFuelTank`, `parseFuelType`); sunucu yeniden denetler. Sayı alanları metindir (kanonik
 * ondalık, ADR 0117 §11): Türkçe girişteki tek virgül ondalık ayırıcı sayılır.
 */

export interface TankFormValues {
  code: string;
  name: string;
  role: string;
  location: string;
  allowedFuelTypeIds: string[];
  consumerIds: string[];
  description: string;
  capacityM3: string;
  fillLimitPct: string;
  unusableM3: string;
  lowLevel: string;
  highLevel: string;
  levelUnit: string;
  measurementMethod: string;
  active: boolean;
}

export const EMPTY_TANK: TankFormValues = {
  code: '',
  name: '',
  role: '',
  location: '',
  allowedFuelTypeIds: [],
  consumerIds: [],
  description: '',
  capacityM3: '',
  fillLimitPct: '',
  unusableM3: '',
  lowLevel: '',
  highLevel: '',
  levelUnit: 'pct',
  measurementMethod: '',
  active: true,
};

/** Açıklamanın üst sınırı (ekrandaki sayaç "63/500"). */
export const TANK_DESCRIPTION_MAX = 500;

export function tankFormOf(tank: FuelTank): TankFormValues {
  return {
    code: tank.code,
    name: tank.name,
    role: tank.role,
    location: tank.location,
    allowedFuelTypeIds: [...tank.allowedFuelTypeIds],
    consumerIds: [...tank.consumerIds],
    description: tank.description ?? '',
    capacityM3: tank.capacityM3,
    fillLimitPct: tank.fillLimitPct,
    unusableM3: tank.unusableM3,
    lowLevel: tank.lowLevel ?? '',
    highLevel: tank.highLevel ?? '',
    levelUnit: tank.levelUnit,
    measurementMethod: tank.measurementMethod,
    active: tank.active,
  };
}

/** Sayı metni: boşluklar atılır; tek virgül (nokta yoksa) ondalık ayırıcıdır. */
export function decimalText(value: string): string {
  const text = value.trim().replace(/\s/g, '');
  return !text.includes('.') && (text.match(/,/g) ?? []).length === 1
    ? text.replace(',', '.')
    : text;
}

const optional = (value: string) => (value.trim() === '' ? null : value.trim());
const optionalNumber = (value: string) => (value.trim() === '' ? null : decimalText(value));

export function tankInputOf(values: TankFormValues): FuelTankInput {
  return {
    code: values.code.trim().toUpperCase(),
    name: values.name.trim(),
    role: values.role,
    location: values.location,
    allowedFuelTypeIds: values.allowedFuelTypeIds,
    consumerIds: values.consumerIds,
    description: optional(values.description),
    capacityM3: decimalText(values.capacityM3),
    fillLimitPct: decimalText(values.fillLimitPct),
    unusableM3: optionalNumber(values.unusableM3),
    lowLevel: optionalNumber(values.lowLevel),
    highLevel: optionalNumber(values.highLevel),
    levelUnit: values.levelUnit,
    measurementMethod: values.measurementMethod,
    active: values.active,
  };
}

/** Form alanının hata kodu (domain şeması); geçerliyse boş. */
export function tankFormIssues(
  values: TankFormValues,
): Partial<Record<keyof TankFormValues, string>> {
  const parsed = parseFuelTank({
    ...tankInputOf(values),
    // Seçilmemiş seçenek "zorunlu" hatası verir
    role: values.role || undefined,
    location: values.location || undefined,
    measurementMethod: values.measurementMethod || undefined,
  });
  if (parsed.ok) return {};
  const issues: Partial<Record<keyof TankFormValues, string>> = {};
  for (const issue of parsed.issues) {
    const key = issue.path as keyof TankFormValues;
    if (key in EMPTY_TANK && issues[key] === undefined) issues[key] = issue.code;
  }
  return issues;
}

/** Sunucunun alan hatası ayrıntısı (`alan:KOD,…`) → alan hataları. */
export function detailIssues(detail: unknown): Record<string, string> {
  if (typeof detail !== 'string') return {};
  const issues: Record<string, string> = {};
  for (const part of detail.split(',')) {
    const [path, code] = part.split(':');
    if (path && code && issues[path] === undefined) issues[path] = code;
  }
  return issues;
}

export interface FuelTypeFormValues {
  code: string;
  name: string;
  category: string;
  grade: string;
  factorClass: string;
  active: boolean;
  remarks: string;
}

export const EMPTY_FUEL_TYPE: FuelTypeFormValues = {
  code: '',
  name: '',
  category: '',
  grade: '',
  factorClass: '',
  active: true,
  remarks: '',
};

export function fuelTypeFormOf(fuel: FuelType): FuelTypeFormValues {
  return {
    code: fuel.code,
    name: fuel.name,
    category: fuel.category,
    grade: fuel.grade ?? '',
    factorClass: fuel.factorClass ?? '',
    active: fuel.active,
    remarks: fuel.remarks ?? '',
  };
}

export function fuelTypeInputOf(values: FuelTypeFormValues): FuelTypeInput {
  return {
    code: values.code.trim().toUpperCase(),
    name: values.name.trim(),
    category: values.category,
    grade: optional(values.grade),
    factorClass: values.factorClass === '' ? null : values.factorClass,
    active: values.active,
    remarks: optional(values.remarks),
  };
}

export function fuelTypeFormIssues(
  values: FuelTypeFormValues,
): Partial<Record<keyof FuelTypeFormValues, string>> {
  const parsed = parseFuelType({
    ...fuelTypeInputOf(values),
    category: values.category || undefined,
  });
  if (parsed.ok) return {};
  const issues: Partial<Record<keyof FuelTypeFormValues, string>> = {};
  for (const issue of parsed.issues) {
    const key = issue.path as keyof FuelTypeFormValues;
    if (key in EMPTY_FUEL_TYPE && issues[key] === undefined) issues[key] = issue.code;
  }
  return issues;
}

/** Tank listesinde arama: ad ya da kod (büyük-küçük harf duyarsız). */
export function matchesTank(tank: Pick<FuelTank, 'code' | 'name'>, query: string): boolean {
  const needle = query.trim().toLocaleLowerCase('tr');
  if (needle === '') return true;
  return (
    tank.code.toLocaleLowerCase('tr').includes(needle) ||
    tank.name.toLocaleLowerCase('tr').includes(needle)
  );
}
